import React from 'react';
import { useMutation } from '@apollo/client';
import TrackerShoppingListQuery from './TrackerShoppingListQuery';
import {
  MarkItemAsBoughtMutation,
  MarkItemAsNotBoughtMutation,
} from './TrackerShoppingListMarkItemMutations';
import type { MarkItemInput, MarkItemResult, TrackerShoppingList } from './types';

const SUCCESS_TYPENAME = 'DailyTrackerShoppingListMarkItemSuccess';

type QueryData = { dailyTracker: { shoppingList: TrackerShoppingList | null } };

interface ITrackerShoppingListMarkItemConnectorApolloProps {
  children: ({
    markItem,
  }: {
    markItem: (input: MarkItemInput) => Promise<MarkItemResult>;
  }) => React.ReactElement;
}

const TrackerShoppingListMarkItemConnectorApollo: React.FC<
  ITrackerShoppingListMarkItemConnectorApolloProps
> = ({ children }) => {
  const [markAsBought] = useMutation(MarkItemAsBoughtMutation);
  const [markAsNotBought] = useMutation(MarkItemAsNotBoughtMutation);

  const markItem = async ({
    itemId,
    startDate,
    endDate,
    isBought,
  }: MarkItemInput): Promise<MarkItemResult> => {
    const mutate = isBought ? markAsBought : markAsNotBought;
    const field = isBought ? 'shoppingListMarkItemAsBought' : 'shoppingListMarkItemAsNotBought';

    try {
      const response = await mutate({
        variables: { input: { startDate, endDate, itemId } },
        update: (cache, { data }) => {
          if (data?.dailyTracker?.[field]?.__typename !== SUCCESS_TYPENAME) return;

          // Items are not normalized (their id repeats across ranges), so the new state is
          // written into every cached list of the mutated range: the explicit-range entry and
          // the argument-less entry when the server resumed this same range. The screen may
          // have switched ranges meanwhile, so the range comes from the mutation input.
          [{ startDate, endDate }, {}].forEach((variables) => {
            cache.updateQuery<QueryData>(
              { query: TrackerShoppingListQuery, variables },
              (existing) => {
                const list = existing?.dailyTracker?.shoppingList;
                if (!existing || !list) return existing;
                if (list.startDate !== startDate || list.endDate !== endDate) return existing;

                return {
                  ...existing,
                  dailyTracker: {
                    ...existing.dailyTracker,
                    shoppingList: {
                      ...list,
                      items: list.items.map((item) =>
                        item.id === itemId ? { ...item, isBought } : item
                      ),
                    },
                  },
                };
              }
            );
          });
        },
      });

      const payload = response.data?.dailyTracker?.[field];

      if (payload?.__typename === SUCCESS_TYPENAME) {
        return { ok: true };
      }

      const message =
        payload?.description || payload?.name || String(payload?.authCode ?? 'mark_server_error');
      return { ok: false, reason: 'server', message };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'mark_network_error';
      return { ok: false, reason: 'network', message };
    }
  };

  return children({ markItem });
};

export default TrackerShoppingListMarkItemConnectorApollo;
